/**
 * `growth/gate` — eligibility of an ad. **PURE** function (no SDK, no clock,
 * no storage): takes the state and returns `{ allow, reason }`.
 *
 * Implements the COMPLETE frequency capping table of the ads strategy:
 *
 * | Rule                                    | Default |
 * |-----------------------------------------|---------|
 * | Interstitial per session                | 1, and only with ≥1 value unit |
 * | Interstitial per day                    | 3 |
 * | Minimum gap between interstitials       | ≥10 min |
 * | First interstitial of one's life        | never in the 1st session |
 * | Native per screen                       | 1 |
 * | Banner visible                          | 1 |
 * | Rewarded                                | 3 rewards/day PER TYPE |
 * | Ignored rewarded offer                  | 2/day, with backoff to 1 after 3 days |
 * | Global interruption budget              | ≤1 interruptive / 10 active min |
 * | Premium                                 | 0 ads, immediate |
 *
 * The `reason` is a stable code (not a UI text): it feeds telemetry and
 * is what the tests assert.
 *
 * The DAILY counters arrive already normalized by `AdCapsStore` (which resets
 * on day change): the gate does not know time zones on purpose.
 */

import type { AdCapPolicy, AdGateDecision, AdGateInput, AdGateReason, AdKind } from "./types";

const MINUTE_MS = 60_000;

/** Portfolio defaults (§4 of the strategy). Config-over-code: each app
 *  can override them per placement without touching this function. */
export const DEFAULT_AD_CAP_POLICY: AdCapPolicy = {
  interstitialPerSession: 1,
  interstitialPerDay: 3,
  minGapBetweenInterstitialsMs: 10 * MINUTE_MS,
  allowInterstitialOnFirstSession: false,
  minUnitsCompleted: 1,
  rewardsPerDayPerType: 3,
  offersPerDayPerPlacement: 2,
  ignoredDaysBeforeBackoff: 3,
  offersPerDayWhenBackedOff: 1,
  nativePerScreen: 1,
  bannersVisible: 1,
  interruptionBudgetMs: 10 * MINUTE_MS,
};

/**
 * Minors regime (COPPA / Play Families / App Store Kids): MORE strict than
 * the general one. The interstitial only lives `between-screens` (never inside a
 * game), max 1/session and 2/day; native is not used; rewarded requires an adult
 * gate and is off by default.
 */
export const CHILD_AD_CAP_POLICY: AdCapPolicy = {
  ...DEFAULT_AD_CAP_POLICY,
  interstitialPerSession: 1,
  interstitialPerDay: 2,
  rewardsPerDayPerType: 1,
};

/** Legal formats in the child regime (§6). */
const CHILD_ALLOWED_KINDS: ReadonlySet<AdKind> = new Set(["banner", "interstitial", "rewarded"]);

function deny(reason: AdGateReason): AdGateDecision {
  return { allow: false, reason };
}

const ALLOW: AdGateDecision = { allow: true, reason: "allow" };

function interstitialDecision(i: AdGateInput, p: AdCapPolicy): AdGateDecision {
  const { caps, session } = i;

  // The first-run is fully respected: native/banner can live on day 1, the
  // interstitial cannot. Burning the first session kills retention (and LTV).
  if (session.isFirstEver && !p.allowInterstitialOnFirstSession) {
    return deny("first-session");
  }
  // An interstitial is only legal AFTER closing a value unit: that is what
  // turns "finished screen" into a breather and not an interruption.
  if (session.unitsCompleted < p.minUnitsCompleted) {
    return deny("no-value-unit");
  }
  if (caps.interstitialsThisSession >= p.interstitialPerSession) {
    return deny("session-cap");
  }
  if (caps.interstitialsToday >= p.interstitialPerDay) {
    return deny("daily-cap");
  }
  if (
    caps.lastInterstitialAt !== undefined &&
    i.now - caps.lastInterstitialAt < p.minGapBetweenInterstitialsMs
  ) {
    return deny("min-gap");
  }
  // Global budget: ≤1 interruptive impression for every `interruptionBudgetMs`
  // of ACTIVE use. The first of the session is free (0 × budget ≤ activeMs);
  // from there on each new interruption must have been "earned" with real use.
  if (caps.interstitialsThisSession * p.interruptionBudgetMs > session.activeMs) {
    return deny("interruption-budget");
  }
  return ALLOW;
}

function rewardedDecision(i: AdGateInput, p: AdCapPolicy): AdGateDecision {
  const { caps, placementId, rewardType } = i;

  // Anti-farming: the cap is per reward TYPE, not per placement — otherwise,
  // two placements that give the same thing would double the ceiling.
  const type = rewardType ?? placementId;
  if ((caps.rewardsToday[type] ?? 0) >= p.rewardsPerDayPerType) {
    return deny("reward-daily-cap");
  }

  // Backoff: if the user has been ignoring the offer for days, it is shown less.
  const ignored = caps.ignoredDays[placementId] ?? 0;
  const offerLimit =
    ignored >= p.ignoredDaysBeforeBackoff
      ? p.offersPerDayWhenBackedOff
      : p.offersPerDayPerPlacement;
  const offers = caps.offersToday[placementId] ?? 0;
  if (offers >= offerLimit) {
    return deny(ignored >= p.ignoredDaysBeforeBackoff ? "offer-backoff" : "offer-daily-cap");
  }
  return ALLOW;
}

/**
 * Is it time to show this ad NOW?
 *
 * Order of the denials on purpose: first the absolute ones (premium, task in
 * progress, minors) and then capping. That way the `reason` that reaches telemetry
 * states the REAL cause and not "cap reached" when it was actually premium.
 */
export function adGate(input: AdGateInput): AdGateDecision {
  const policy: AdCapPolicy = {
    ...(input.user.child ? CHILD_AD_CAP_POLICY : DEFAULT_AD_CAP_POLICY),
    ...input.policy,
  };

  // 1. Premium removes ads in the WHOLE app, immediate and without waiting for sync
  //    (the optimistic local flag wins after purchase).
  if (input.user.premium) {
    return deny("premium");
  }

  // 2. Never in the middle of a task: active input, timer running, transaction
  //    half-done or gesture in progress. It wins over any capping schedule.
  if (input.inFlight) {
    return deny("in-flight");
  }

  // 3. Minors regime.
  if (input.user.child) {
    if (!CHILD_ALLOWED_KINDS.has(input.kind)) {
      return deny("child-policy");
    }
    if (input.kind === "interstitial" && input.breakpoint !== "between-screens") {
      // Never inside the game: only the transition between screens.
      return deny("child-policy");
    }
    if (input.kind === "rewarded" && !input.user.adultGateCleared) {
      // Rewarded off by default: only after parental gate.
      return deny("child-policy");
    }
  }

  switch (input.kind) {
    case "interstitial": {
      return interstitialDecision(input, policy);
    }
    case "rewarded": {
      return rewardedDecision(input, policy);
    }
    case "native": {
      return input.caps.nativesOnScreen >= policy.nativePerScreen ? deny("screen-cap") : ALLOW;
    }
    case "banner": {
      return input.caps.bannersVisible >= policy.bannersVisible ? deny("screen-cap") : ALLOW;
    }
    default: {
      return deny("child-policy");
    }
  }
}
